import { environment } from 'environments/environment';
import { PlanEnum } from 'app/enums/plan.enum';

/**
 * Utility class for managing resource URLs based on partner plan
 * PRO partners get CDN URLs for better performance, others get bucket URLs
 */
export class ResourceUtils {
  
  /**
   * Determines if the given plan code has PRO access
   * @param planCode The plan code to check
   * @returns true if the plan has PRO access, false otherwise
   */
  static hasProAccess(planCode: string): boolean {
    if (!planCode) {
      return false;
    }
    
    // Check directly the planCode using enum
    // PRO plan users get CDN access, all others use bucket
    return planCode === PlanEnum.PRO;
  }
  
  /**
   * Get the appropriate base URL for resources based on partner plan
   * PRO partners get CDN URLs, others get bucket URLs
   * @param planCode The plan code to determine the resource URL
   * @returns The base URL (CDN or bucket) for resources
   */
  static getResourceBaseUrl(planCode: string): string {
    const hasProAccess = this.hasProAccess(planCode);
    
    // Log for debugging (can be removed in production)
    if (console && console.debug) {
      console.debug(`[ResourceUtils] Plan ${planCode} has PRO access: ${hasProAccess}, using ${hasProAccess ? 'CDN' : 'bucket'}`);
    }
    
    return hasProAccess ? environment.cdn : environment.bucket;
  }
  
  /**
   * Construct a complete resource URL based on partner plan
   * @param planCode The plan code to determine the resource URL
   * @param resourcePath The path to the resource
   * @returns The complete URL to the resource
   */
  static getResourceUrl(planCode: string, resourcePath: string): string {
    if (!resourcePath) {
      return '';
    }
    
    const baseUrl = this.getResourceBaseUrl(planCode);
    // Ensure proper URL construction
    const cleanPath = resourcePath.startsWith('/') ? resourcePath.substring(1) : resourcePath;
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
    
    return cleanBaseUrl + cleanPath;
  }
  
  /**
   * Check if CDN is available in the current environment
   * @returns true if CDN is configured, false otherwise
   */
  static isCdnAvailable(): boolean {
    return !!(environment.cdn && environment.cdn.trim().length > 0);
  }
  
  /**
   * Get resource URL with fallback logic
   * @param planCode The plan code to determine the resource URL
   * @param resourcePath The path to the resource
   * @param forceBucket Force using bucket instead of CDN (useful for admin interfaces)
   * @returns The complete URL to the resource
   */
  static getResourceUrlWithFallback(planCode: string, resourcePath: string, forceBucket: boolean = false): string {
    if (!resourcePath) {
      return '';
    }
    
    let baseUrl = environment.bucket; // Default fallback
    
    if (!forceBucket && this.isCdnAvailable() && this.hasProAccess(planCode)) {
      baseUrl = environment.cdn;
    }
    
    // Ensure proper URL construction
    const cleanPath = resourcePath.startsWith('/') ? resourcePath.substring(1) : resourcePath;
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
    
    return cleanBaseUrl + cleanPath;
  }
} 